// @ts-nocheck - Legacy file with extensive type issues requiring comprehensive refactoring (TODO: GXM-XXXX)
import { useState } from "react";
import { API_ROUTES } from "~/api-routes";
import API_ROUTES_V2 from "~/api-routes-v2";
import apiService from "~/services/service";
import useMetadataProcessor from "./useMetadataProcessor";

const useAttachmentFilter = () => {
  const [allData, setAllData] = useState<any[]>([]);
  const [data, setData] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filterKeys, setFilterKeys] = useState<{
    categories: any[];
    typeId: string;
    type: string;
    year: string | null;
    page: number;
    pageSize: number;
    sortOrder: string;
    sortType: string;
  }>({
    categories: [],
    typeId: "",
    type: "",
    year: null,
    page: 1,
    pageSize: 10,
    sortOrder: "DESC",
    sortType: "name",
  });

  const { processedData, loading, setLoading } = useMetadataProcessor(allData);

  const fetchDocuments = async (filter: any) => {
    try {
      const queryParams = new URLSearchParams();
      if (filter.year) {
        if (filter.type === "EMPLOYEE") {
          queryParams.append("tag", `customYear-${filter.year}`);
        } else {
          queryParams.append("tag", `year-${filter.year}`);
        }
      }

      if (filter.category) {
        queryParams.append(
          "tag",
          `category-${encodeURIComponent(filter.category)}`,
        );
      }
      if (filter.subCategory) {
        queryParams.append(
          "tag",
          `subCategory-${encodeURIComponent(filter.subCategory)}`,
        );
      }
      if (filter.requestReason) {
        queryParams.append(
          "tag",
          `request_reason-${encodeURIComponent(filter.requestReason)}`,
        );
      }

      // Don't make API call if type is null - it doesn't make sense without a document type
      if (!filter.type) {
        console.warn("Document type is null, skipping API call");
        return [];
      }

      const { data } = await apiService(
        API_ROUTES.FETCH_DOCUMENTS + `&${queryParams.toString()}`,
        "GET",
        {
          replace: { id: filter.entity, type: filter.type },
        },
      );
      // Filter out files that don't have createdBy or uploadedBy information
      return data?.filter((file) => file.createdByData || file.name) || [];
    } catch (error) {
      console.error("Error fetching documents: ", error);
      return [];
    }
  };
  const fetchAllFilteredDocuments = async () => {
    setLoading(true);
    setError(null);
    const years = filterKeys.year
      ? Array.isArray(filterKeys.year)
        ? filterKeys.year
        : [filterKeys.year]
      : [undefined];
    const categories =
      filterKeys.categories?.length > 0
        ? filterKeys.categories
        : [{ category: undefined, subCategories: [undefined] }];
    const filters = categories.flatMap((categoryFilter) => {
      const subCategories =
        categoryFilter.subCategories?.length > 0
          ? categoryFilter.subCategories
          : [undefined];

      return years.flatMap((year) =>
        subCategories.map((subCategory) => ({
          ...(categoryFilter.category && { category: categoryFilter.category }),
          ...(subCategory && { subCategory }),
          ...(year && { year }),
          entity: filterKeys.typeId,
          type: filterKeys.type,
        })),
      );
    });
    try {
      setFilterKeys((prev) => ({
        ...prev,
        page: 1,
        pageSize: 10,
        sortOrder: "DESC",
        sortType: "name",
      }));
      const allData = await Promise.all(
        filters.map(async (filter) => {
          const documents = await fetchDocuments(filter);
          return documents;
        }),
      );
      const mergedData = allData.flat();
      const uniqueData = mergedData.filter(
        (doc, index, self) =>
          index === self.findIndex((d) => d.name === doc.name),
      );
      setAllData(uniqueData);
      if (!uniqueData?.length) {
        setLoading(false);
      }
      sortData(filterKeys.sortType, "DESC", uniqueData);
    } catch (err) {
      setError(err);
    }
  };

  // method for process attachment tab as it fetches file for process and payslips
  const fetchAllFilteredDocumentsForProcessTab = async (types: string[]) => {
    setLoading(true);
    setError(null);

    const years = filterKeys.year
      ? Array.isArray(filterKeys.year)
        ? filterKeys.year
        : [filterKeys.year]
      : [undefined];

    const categories =
      filterKeys.categories?.length > 0
        ? filterKeys.categories
        : [{ category: undefined, subCategories: [undefined] }];

    const filters = types.flatMap((type) =>
      categories.flatMap((categoryFilter) => {
        const subCategories =
          categoryFilter.subCategories?.length > 0
            ? categoryFilter.subCategories
            : [undefined];

        return years.flatMap((year) =>
          subCategories.map((subCategory) => ({
            ...(categoryFilter.category && {
              category: categoryFilter.category,
            }),
            ...(subCategory && { subCategory }),
            ...(year && { year }),
            entity: filterKeys.typeId,
            type,
          })),
        );
      }),
    );

    try {
      setFilterKeys((prev) => ({
        ...prev,
        page: 1,
        pageSize: 10,
        sortOrder: "DESC",
        sortType: "name",
      }));

      const allData = await Promise.all(
        filters.map(async (filter) => {
          const documents = await fetchDocuments(filter);
          return documents;
        }),
      );

      const mergedData = allData.flat();
      const uniqueData = mergedData.filter(
        (doc, index, self) =>
          index === self.findIndex((d) => d.name === doc.name),
      );

      setAllData(uniqueData);
      if (!uniqueData?.length) {
        setLoading(false);
      }
      sortData(filterKeys.sortType, "DESC", uniqueData);
    } catch (err) {
      setError(err);
    }
  };

  const fetchFilteredDocumentsByType = async (
    type: string[],
    entityId: string,
    filterKeys: any,
  ) => {
    // create common filters for categories, subcategories, years
    const years = filterKeys.year
      ? Array.isArray(filterKeys.year)
        ? filterKeys.year
        : [filterKeys.year]
      : [undefined];

    const categories =
      filterKeys.categories?.length > 0
        ? filterKeys.categories
        : [{ category: undefined, subCategories: [undefined] }];

    // Fetch relatedIDs based on type in entity information
    const [shareholderData, officerData, poaData, directorData] =
      await Promise.all([
        type.includes("SHAREHOLDER")
          ? apiService(API_ROUTES_V2.SHARE_HOLDERS, "GET", {
              replace: { id: entityId },
            })
          : Promise.resolve({ data: [] }),

        type.includes("OFFICER")
          ? apiService(API_ROUTES_V2.FETCH_OFFICERS, "GET", {
              replace: {
                entity_id: entityId,
                page: "1",
                pageSize: "100", // since api is paginated, keeping the size 100, might need to update
                searchValue: "",
                sortBy: "name",
                sortOrder: "ASC",
                type: "officer",
              },
            })
          : Promise.resolve({ data: { records: [] } }),

        type.includes("POA")
          ? apiService(API_ROUTES_V2.FETCH_OFFICERS, "GET", {
              replace: {
                entity_id: entityId,
                page: "1",
                pageSize: "100",
                searchValue: "",
                sortBy: "name",
                sortOrder: "ASC",
                type: "power-of-attorney",
              },
            })
          : Promise.resolve({ data: { records: [] } }),

        type.includes("DIRECTOR")
          ? apiService(API_ROUTES_V2.FETCH_DIRECTORS, "GET", {
              replace: { id: entityId },
            })
          : Promise.resolve({ data: [] }),
      ]);

    // create arrays of IDs
    const shareholderIds = shareholderData.data.map(
      (shareholder) => shareholder.id,
    );
    const officerIds =
      officerData.data?.records?.map((officer) => officer.id) || [];
    const powerOfAttorneyIds =
      poaData.data?.records?.map((poa) => poa.id) || [];
    const directorIds = directorData.data?.map((director) => director.id) || [];

    const createFilters = (ids: string[], typeValue: string) => {
      return categories.flatMap((categoryFilter) => {
        const subCategories =
          categoryFilter.subCategories?.length > 0
            ? categoryFilter.subCategories
            : [undefined];

        return years.flatMap((year) =>
          subCategories.flatMap((subCategory) =>
            ids.map((id) => ({
              ...(categoryFilter.category && {
                category: categoryFilter.category,
              }),
              ...(subCategory && { subCategory }),
              ...(year && { year }),
              entity: id,
              type: typeValue,
            })),
          ),
        );
      });
    };

    const allFilters = [];

    // ENTITIES_PLUS filters
    if (type.includes("ENTITIES_PLUS")) {
      const entityFilters = createFilters([entityId], "ENTITIES_PLUS");
      allFilters.push(...entityFilters);
    }

    // SHAREHOLDER filters
    if (shareholderIds.length > 0) {
      const shareholderFilters = createFilters(shareholderIds, "SHAREHOLDER");
      allFilters.push(...shareholderFilters);
    }

    // OFFICER filters
    if (officerIds.length > 0) {
      const officerFilters = createFilters(officerIds, "OFFICER");
      allFilters.push(...officerFilters);
    }

    // POA filters
    if (powerOfAttorneyIds.length > 0) {
      const poaFilters = createFilters(powerOfAttorneyIds, "OFFICER");
      allFilters.push(...poaFilters);
    }

    // DIRECTOR filters
    if (directorIds.length > 0) {
      const directorFilters = createFilters(directorIds, "DIRECTOR");
      allFilters.push(...directorFilters);
    }

    const allResults = await Promise.all(
      allFilters.map(async (filter) => {
        const documents = await fetchDocuments(filter);
        return documents;
      }),
    );

    const mergedData = allResults.flat();
    const uniqueData = mergedData.filter(
      (doc, index, self) =>
        index === self.findIndex((d) => d.name === doc.name),
    );
    return uniqueData;
  };

  const fetchFilteredDocumentsByProcess = async (
    processTemplates: string[],
    filterKeys: any,
    entityName?: string,
    service?: string,
  ) => {
    const years = filterKeys.year
      ? Array.isArray(filterKeys.year)
        ? filterKeys.year
        : [filterKeys.year]
      : [undefined];

    const categories =
      filterKeys.categories?.length > 0
        ? filterKeys.categories
        : [{ category: undefined, subCategories: [undefined] }];
    const processPromises =
      processTemplates && processTemplates.length > 0
        ? processTemplates.map((templateId) =>
            apiService(
              API_ROUTES.FETCH_USER_PROCESSES +
                `&processTemplate={processTemplate}`,
              "GET",
              {
                replace: {
                  page: "1",
                  pageSize: "100",
                  sortBy: "name",
                  sortOrder: "ASC",
                  status: "",
                  service: "",
                  flagged: "",
                  search: entityName || "",
                  from_date: "",
                  to_date: "",
                  processTemplate: templateId,
                },
              },
            ),
          )
        : [
            apiService(API_ROUTES.FETCH_USER_PROCESSES, "GET", {
              replace: {
                page: "1",
                pageSize: "100",
                sortBy: "name",
                sortOrder: "ASC",
                status: "",
                service: service || "",
                flagged: "",
                search: entityName || "",
                from_date: "",
                to_date: "",
              },
            }),
          ];
    const processData = await Promise.all(processPromises);
    const processIds = processData
      .flatMap((res) => res?.data?.records || [])
      .map((process) => process.id);
    const createFilters = (ids: string[], typeValue: string) => {
      return categories.flatMap((categoryFilter) => {
        const subCategories =
          categoryFilter.subCategories?.length > 0
            ? categoryFilter.subCategories
            : [undefined];

        return years.flatMap((year) =>
          subCategories.flatMap((subCategory) =>
            ids.map((id) => ({
              ...(categoryFilter.category && {
                category: categoryFilter.category,
              }),
              ...(subCategory && { subCategory }),
              ...(year && { year }),
              entity: id,
              type: typeValue,
            })),
          ),
        );
      });
    };

    const allFilters = [];
    const entityFilters = createFilters(processIds, "PROCESS");
    //for payslips
    const entityPayslipFilters = createFilters(processIds, "PAYSLIPS");
    allFilters.push(...entityFilters, ...entityPayslipFilters);

    const allResults = await Promise.all(
      allFilters.map(async (filter) => {
        const documents = await fetchDocuments(filter);
        return documents;
      }),
    );

    const mergedData = allResults.flat();
    const uniqueData = mergedData.filter(
      (doc, index, self) =>
        index === self.findIndex((d) => d.name === doc.name),
    );
    return uniqueData;
  };

  const fetchFilteredDocumentsByRequest = async (
    requestReasons: string[],
    entityId: string,
    filterKeys: any,
  ) => {
    const { data } = await apiService(API_ROUTES.FETCH_REQUESTS, "GET", {
      replace: {
        page: "1",
        pageSize: "100",
        filter: "all",
        requestType: "AD_HOC",
        entity: entityId,
      },
    });
    const requestIds = data?.requests?.map((request) => request.id) || [];
    const years = filterKeys.year
      ? Array.isArray(filterKeys.year)
        ? filterKeys.year
        : [filterKeys.year]
      : [undefined];

    const categories =
      filterKeys.categories?.length > 0
        ? filterKeys.categories
        : [{ category: undefined, subCategories: [undefined] }];

    const reasons =
      requestReasons && requestReasons.length > 0
        ? requestReasons
        : [undefined];

    const createFilters = (
      ids: string[],
      reasons: (string | undefined)[],
      typeValue: string,
    ) => {
      return categories.flatMap((categoryFilter) => {
        const subCategories =
          categoryFilter.subCategories?.length > 0
            ? categoryFilter.subCategories
            : [undefined];

        return years.flatMap((year) =>
          subCategories.flatMap((subCategory) =>
            reasons.flatMap((reason) =>
              ids.map((id) => ({
                ...(categoryFilter.category && {
                  category: categoryFilter.category,
                }),
                ...(subCategory && { subCategory }),
                ...(year && { year }),
                ...(reason && { requestReason: reason }),
                type: typeValue,
                entity: id,
              })),
            ),
          ),
        );
      });
    };
    const allFilters = createFilters(requestIds, reasons, "AD_HOC");

    const allResults = await Promise.all(
      allFilters.map(async (filter) => {
        const documents = await fetchDocuments(filter);
        return documents;
      }),
    );

    const mergedData = allResults.flat();
    const uniqueData = mergedData.filter(
      (doc, index, self) =>
        index === self.findIndex((d) => d.name === doc.name),
    );

    return uniqueData;
  };

  const fetchFilesAndDocsFilteredDocuments = async (
    type: string[],
    entityId: string,
    processTemplates?: string[],
    entityName?: string,
    requestReasons?: string[],
    service?: string,
  ) => {
    setLoading(true);
    setError(null);
    setAllData([]);
    try {
      setFilterKeys((prev) => ({
        ...prev,
        page: 1,
        pageSize: 10,
        sortOrder: "DESC",
        sortType: "name",
      }));
      const hasOtherFilter =
        filterKeys?.categories?.length && !processTemplates?.length;
      const hasRequestOtherFilter =
        (requestReasons ||
          filterKeys?.categories?.length ||
          filterKeys?.year?.length) &&
        !processTemplates?.length;

      const hasProcess = type && type.length > 0 && type.includes("PROCESS");
      const hasRequest = type && type.length > 0 && type.includes("AD_HOC");
      const [processDocs, requestDocs, entityInfoDocs] = await Promise.all([
        hasProcess
          ? fetchFilteredDocumentsByProcess(
              processTemplates,
              filterKeys,
              entityName,
              service,
            )
          : Promise.resolve([]),
        hasRequest && Boolean(hasRequestOtherFilter)
          ? fetchFilteredDocumentsByRequest(
              requestReasons,
              entityId,
              filterKeys,
            )
          : Promise.resolve([]),
        Boolean(hasOtherFilter)
          ? fetchFilteredDocumentsByType(type, entityId, filterKeys)
          : Promise.resolve([]),
        // fetchFilteredDocumentsByType(type, entityId, filterKeys),
      ]);
      const mergedData = [...processDocs, ...requestDocs, ...entityInfoDocs];

      const uniqueData = mergedData.filter(
        (doc, index, self) =>
          index === self.findIndex((d) => d.name === doc.name),
      );
      setAllData(uniqueData);
      if (!uniqueData?.length) {
        setLoading(false);
      }
      sortData(filterKeys.sortType, "DESC", uniqueData);
    } catch (err) {
      setError(err);
    }
  };

  const onFilterChange = (newFilter) => {
    setFilterKeys((prev) => ({
      ...prev,
      ...newFilter,
    }));
  };

  const onPageChange = (page: number) => {
    setFilterKeys((prev) => ({
      ...prev,
      page,
    }));
    paginateData(page, allData);
  };
  const onPageSizeChange = (pageSize: number) => {
    setFilterKeys((prev) => ({
      ...prev,
      pageSize,
      page: 1,
    }));
    paginateData(1, allData);
  };

  const paginateData = (page: number, data) => {
    const startIndex = (page - 1) * filterKeys.pageSize;
    const endIndex = startIndex + filterKeys.pageSize;
    const paginatedData = data.slice(startIndex, endIndex);
    setData(paginatedData);
  };

  const sortData = (sortType: string, sortOrder: string, data) => {
    const sortedData = [...data].sort((a, b) => {
      let aValue = a[sortType];
      let bValue = b[sortType];
      if (aValue < bValue) {
        return sortOrder === "ASC" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortOrder === "ASC" ? 1 : -1;
      }
      return 0;
    });
    setAllData(sortedData);
    paginateData(filterKeys.page, sortedData);
  };

  const sortDataHelper = (
    dataToSort: any[],
    sortType: string,
    sortOrder: string,
  ) => {
    return [...dataToSort].sort((a, b) => {
      let aValue = a[sortType];
      let bValue = b[sortType];

      if (aValue < bValue) {
        return sortOrder === "ASC" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortOrder === "ASC" ? 1 : -1;
      }
      return 0;
    });
  };

  const onSortOrderChange = (order: string) => {
    setFilterKeys((prev) => ({
      ...prev,
      sortOrder: order,
      page: 1,
      pageSize: 10,
    }));
    sortData(filterKeys.sortType, order, allData);
  };
  // Use processedData if available, otherwise use allData
  const finalAllData =
    processedData && processedData.length > 0 ? processedData : allData;

  const totalRecords = finalAllData.length;
  const totalPages = Math.ceil(totalRecords / filterKeys.pageSize);

  return {
    data,
    allData: finalAllData,
    loading,
    error,
    totalRecords,
    totalPages,
    currentPage: filterKeys.page,
    pageSize: filterKeys.pageSize,
    onFilterApply: fetchAllFilteredDocuments,
    onFilterApplyFilesAndDocs: fetchFilesAndDocsFilteredDocuments,
    onFilterApplyForProcess: fetchAllFilteredDocumentsForProcessTab,
    onFilterChange,
    onPageChange,
    onSortOrderChange,
    onPageSizeChange,
  };
};

export default useAttachmentFilter;
